import React, { useCallback, useEffect } from 'react';
import {
  PublicClientApplication,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { MsalProvider, useMsal } from '@azure/msal-react';
import { msalConfig } from './msalConfig';
import { setTokenRefreshFn } from '../services/api';

const DEV_AUTH_BYPASS = process.env.REACT_APP_DEV_AUTH_BYPASS === 'true';

const msalInstance = DEV_AUTH_BYPASS ? null : new PublicClientApplication(msalConfig);

// ---- Dev-mode auth provider (no MSAL) ----

function DevAuthProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    // In dev mode, register a dummy token refresh so the 401 retry still works
    setTokenRefreshFn(async () => 'dev-token');
  }, []);

  return <>{children}</>;
}

// ---- Production MSAL auth provider ----

function MsalAuthProvider({ children }: { children: React.ReactNode }) {
  const [isInitialized, setIsInitialized] = React.useState(false);

  React.useEffect(() => {
    msalInstance!.initialize().then(() => {
      return msalInstance!.handleRedirectPromise();
    }).then(() => {
      setIsInitialized(true);
    }).catch((error) => {
      console.error('MSAL initialization error:', error);
      setIsInitialized(true);
    });
  }, []);

  if (!isInitialized) return null;

  return (
    <MsalProvider instance={msalInstance!}>
      <TokenRefreshBridge />
      {children}
    </MsalProvider>
  );
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  if (DEV_AUTH_BYPASS) {
    return <DevAuthProvider>{children}</DevAuthProvider>;
  }
  return <MsalAuthProvider>{children}</MsalAuthProvider>;
}

/** Registers the token refresh function with the API layer for automatic 401 retry */
function TokenRefreshBridge() {
  const { instance, accounts } = useMsal();

  useEffect(() => {
    setTokenRefreshFn(async () => {
      if (accounts.length === 0) throw new Error('Not authenticated');
      try {
        const result = await instance.acquireTokenSilent({
          scopes: ['openid', 'profile', 'email'],
          account: accounts[0],
          forceRefresh: true,
        });
        return result.idToken;
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          const result = await instance.acquireTokenPopup({
            scopes: ['openid', 'profile', 'email'],
          });
          return result.idToken;
        }
        throw error;
      }
    });
  }, [instance, accounts]);

  return null;
}

/** Hook for MSAL token acquisition (only used when NOT in dev mode) */
function useMsalApiToken(): (forceRefresh?: boolean) => Promise<string> {
  const { instance, accounts } = useMsal();
  return useCallback(async (forceRefresh?: boolean) => {
    if (accounts.length === 0) throw new Error('Not authenticated');
    try {
      const result = await instance.acquireTokenSilent({
        scopes: ['openid', 'profile', 'email'],
        account: accounts[0],
        forceRefresh: forceRefresh || false,
      });
      return result.idToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        const result = await instance.acquireTokenPopup({
          scopes: ['openid', 'profile', 'email'],
        });
        return result.idToken;
      }
      throw error;
    }
  }, [instance, accounts]);
}

/** Dev-mode token hook — returns a dummy token without MSAL */
function useDevApiToken(): (forceRefresh?: boolean) => Promise<string> {
  return useCallback(async () => 'dev-token', []);
}

export function useApiToken(): (forceRefresh?: boolean) => Promise<string> {
  if (DEV_AUTH_BYPASS) return useDevApiToken();
  return useMsalApiToken();
}

